import { useEffect, type ReactElement } from 'react';

import { createPortal } from 'react-dom';

import { ReactComponent as CheckIcon } from '../../../../assets/icons/check.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info.svg';
import { ReactComponent as WarningIcon } from '../../../../assets/icons/warning.svg';
import { CLOSE_ICON } from '../../../constants/images';
import type { IToastProps } from '../../../interfaces/globals';
import type { StatusVariant } from '../../../types/globals';
import IconButton from '../../Buttons/IconButton/IconButton';
import './Toast.scss';

const TOAST_ICONS: Record<StatusVariant, ReactElement> = {
  success: <CheckIcon className="toast__icon" />,
  danger: <WarningIcon className="toast__icon" />,
  info: <InfoIcon className="toast__icon" />,
  warning: <WarningIcon className="toast__icon" />
};

export const Toast = ({
  open,
  title,
  message,
  onClose,
  variant = 'success',
  duration = 4500,
  dismissible = true,
  position = 'bottom-center'
}: IToastProps) => {
  useEffect(() => {
    if (!open || duration <= 0) return;
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [open, duration, onClose]);

  if (!open) return null;

  return createPortal(
    <div role="status" aria-live="polite" className={`toast toast--${variant} toast--${position}`}>
      {TOAST_ICONS[variant]}
      {title && <span className="toast__title">{title}</span>}
      <span className="toast__message">{message}</span>
      {dismissible && (
        <IconButton
          className="ml-auto"
          icon={CLOSE_ICON}
          size="s"
          variant="transparentInverse"
          action={onClose}
          label="close"
        />
      )}
    </div>,
    document.body
  );
};

export default Toast;
